import { useEffect, useState } from "react";

import BindingResult from "./BindingResult";
import Loading from "./Loading";
import PhoneNumberVerification from "./PhoneNumberVerification";
import phoneBindingApi from "./services/phoneBindingApi";
import SmsVerification from "./SmsVerification";

type Step =
  | "checking-status"
  | "phone-number"
  | "sms"
  | "loading"
  | "success"
  | "error"
  | "already-linked";

export default function PhoneBinding() {
  const [step, setStep] = useState<Step>("checking-status");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isResendingSms, setIsResendingSms] = useState(false);

  useEffect(() => {
    const checkPhoneBindingStatus = async () => {
      try {
        const { isPhoneBound } = await phoneBindingApi.getPhoneBindingStatus();

        setStep(isPhoneBound ? "already-linked" : "phone-number");
      } catch {
        setErrorMessage(null);
        setStep("error");
      }
    };

    checkPhoneBindingStatus();
  }, []);

  useEffect(() => {
    document.title = "迪士尼美語世界";
  }, []);

  const handlePhoneNumberSubmit = async (value: string) => {
    if (isSendingSms) return;

    setIsSendingSms(true);

    try {
      const result = await phoneBindingApi.sendSms(value);

      if (!result.isSuccess) {
        setErrorMessage(result.message);
        setStep("error");
        return;
      }

      setPhoneNumber(value);
      setStep("sms");
    } catch {
      setErrorMessage("發生異常，請返回 LINE 後重新嘗試");
      setStep("error");
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleVerify = async (verificationCode: string) => {
    setErrorMessage(null);
    setStep("loading");

    try {
      const result = await phoneBindingApi.verify(
        phoneNumber,
        verificationCode,
      );

      if (!result.isSuccess) {
        setErrorMessage(result.message);
        setStep("error");
        return;
      }

      setStep("success");
    } catch {
      setErrorMessage("發生異常，請返回 LINE 後重新嘗試");
      setStep("error");
    }
  };

  const handleResend = async () => {
    if (isResendingSms) return false;

    setIsResendingSms(true);

    try {
      const result = await phoneBindingApi.sendSms(phoneNumber);

      if (!result.isSuccess) {
        setErrorMessage(result.message);
        setStep("error");
        return false;
      }

      return true;
    } catch {
      setErrorMessage("發生異常，請返回 LINE 後重新嘗試");
      setStep("error");

      return false;
    } finally {
      setIsResendingSms(false);
    }
  };

  if (step === "checking-status") return <Loading />;

  if (step === "already-linked") return <BindingResult type="already-linked" />;

  if (step === "phone-number")
    return (
      <PhoneNumberVerification
        isSubmitting={isSendingSms}
        onSubmit={handlePhoneNumberSubmit}
      />
    );

  if (step === "sms")
    return (
      <SmsVerification
        isResending={isResendingSms}
        onVerify={handleVerify}
        onResend={handleResend}
      />
    );

  if (step === "loading") return <Loading message="帳號綁定中..." />;

  if (step === "success") return <BindingResult type="success" />;

  if (step === "error")
    return <BindingResult type="error" message={errorMessage} />;

  return null;
}
